import * as SentryUtils from "../utils/sentry";
import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { Button } from "../components/common/Button";
import {
  ErrorMessage,
  FormContainer,
  FormGroup,
  FormHeading,
  FormText,
  Input,
  Label,
} from "../components/common/Form";
import { useAuthStore } from "../stores/auth.store";
import { Confirmation } from "../components/common/Confirmation";

type Inputs = {
  email: string;
};

export const PasswordRecover = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [rateLimitError, setRateLimitError] = useState<string | null>(null);
  const { recoverPassword } = useAuthStore();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Inputs>();

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    setIsLoading(true);
    setRateLimitError(null);
    try {
      const responseError = await recoverPassword(data.email);
      if (!responseError) {
        setIsSubmitted(true);
      } else if (responseError.status === 429) {
        // Handle rate limiting error
        setRateLimitError(
          "Hai già richiesto un recupero password, controlla la mail o la casella spam.",
        );
      } else {
        // Handle other auth errors
        setRateLimitError("SI È VERIFICATO UN ERRORE. RIPROVA PIÙ TARDI.");
      }
    } catch (error) {
      SentryUtils.captureException(error, {
        tags: { component: "PasswordRecover", operation: "password_reset" }
      });
      console.error("Password recovery failed:", error);
      setRateLimitError(
        "SI È VERIFICATO UN ERRORE IMPREVISTO. RIPROVA PIÙ TARDI.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const confirmMessage =
    "TI ABBIAMO INVIATO UNA EMAIL CON LE ISTRUZIONI PER REIMPOSTARE LA TUA PASSWORD. SEGUI LE ISTRUZIONI PER COMPLETARE IL PROCESSO.";

  return isSubmitted ? (
    <Confirmation title="CONTROLLA LA TUA EMAIL" message={confirmMessage} />
  ) : (
    <FormContainer>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormHeading>RECUPERA PASSWORD</FormHeading>
        <FormText>
          INSERISCI L'INDIRIZZO EMAIL ASSOCIATO AL TUO ACCOUNT. TI INVIEREMO UN
          LINK PER REIMPOSTARE LA TUA PASSWORD.
        </FormText>

        <FormGroup>
          <Label htmlFor="email">Mail</Label>
          <Input
            id="email"
            type="email"
            $hasError={!!errors.email || !!rateLimitError}
            {...register("email", {
              required: "Campo obbligatorio",
              pattern: {
                value: /\S+@\S+\.\S+/,
                message: "Inserisci un'email valida",
              },
            })}
          />
          {errors.email && (
            <ErrorMessage role="alert">{errors.email.message}</ErrorMessage>
          )}
          {rateLimitError && (
            <ErrorMessage role="alert">{rateLimitError}</ErrorMessage>
          )}
        </FormGroup>

        <Button
          type="submit"
          fullWidth
          loading={isLoading}
          loadingText="INVIO IN CORSO..."
        >
          INVIA EMAIL DI RECUPERO
        </Button>
      </form>
    </FormContainer>
  );
};
